import { useCallback, useEffect, useState } from 'react'
import {
  Button,
  Drawer,
  Input,
  Popconfirm,
  Table,
  Tag,
  Typography,
  Upload,
  message,
} from 'antd'
import {
  CloudUploadOutlined,
  DeleteOutlined,
  DiffOutlined,
  EyeOutlined,
  FileOutlined,
  FolderOpenOutlined,
  HistoryOutlined,
  ReloadOutlined,
} from '@ant-design/icons'
import type { UploadProps } from 'antd'
import { api, type PageResult } from '../api'
import { docCompareResultUrl } from '../lib/docCompareResult'
import { formatDate, PageHeader, tablePagination, useServerPagination } from '../components/ui'
import './DocCompare.css'

const { Text } = Typography
const LONG_MS = 120_000
const COMPARE_ACCEPT =
  '.pdf,.doc,.docx,.rtf,.png,.jpg,.jpeg,.webp,.tif,.tiff,.bmp,application/pdf,image/*'

type SideKind = 'upload' | 'library'

type SideState = {
  kind: SideKind
  itemId: number | null
  title: string
}

type LibraryItem = {
  id: number
  title: string
  folder_id: number | null
  index_status: string
  source_type: string
  updated_at: string | null
}

type SessionRunOut = {
  id: number
  status: string
  message?: string
}

type SessionListItem = {
  id: number
  status: string
  left_title: string
  right_title: string
  compare_mode: string
  similarity_percent: number | null
  diff_percent: number | null
  created_at: string | null
  expires_at: string | null
}

const emptySide = (): SideState => ({ kind: 'upload', itemId: null, title: '' })

export default function DocCompare() {
  const [left, setLeft] = useState<SideState>(emptySide())
  const [right, setRight] = useState<SideState>(emptySide())
  const [loading, setLoading] = useState(false)
  const [pickerOpen, setPickerOpen] = useState<'left' | 'right' | null>(null)
  const [libQ, setLibQ] = useState('')
  const [libItems, setLibItems] = useState<LibraryItem[]>([])
  const [libTotal, setLibTotal] = useState(0)
  const [libLoading, setLibLoading] = useState(false)
  const histPag = useServerPagination()
  const libPag = useServerPagination()
  const [histQ, setHistQ] = useState('')
  const [histItems, setHistItems] = useState<SessionListItem[]>([])
  const [histTotal, setHistTotal] = useState(0)
  const [histLoading, setHistLoading] = useState(false)
  const [histDeletingId, setHistDeletingId] = useState<number | null>(null)

  const loadHistory = useCallback((page: number, ps: number, q: string) => {
    setHistLoading(true)
    api
      .get<PageResult<SessionListItem>>('/public/doc-compare/sessions', {
        params: { page, page_size: ps, q: q || undefined },
      })
      .then((r) => {
        setHistItems(r.data.items)
        setHistTotal(r.data.total)
      })
      .catch((e) => message.error(e.message))
      .finally(() => setHistLoading(false))
  }, [])

  useEffect(() => {
    loadHistory(histPag.page, histPag.pageSize, histQ)
  }, [histPag.page, histPag.pageSize, histQ, loadHistory])

  const deleteHistorySession = (id: number) => {
    setHistDeletingId(id)
    api
      .delete(`/public/doc-compare/sessions/${id}`)
      .then(() => {
        message.success('已删除比对记录')
        const nextPage = histItems.length <= 1 && histPag.page > 1 ? histPag.page - 1 : histPag.page
        loadHistory(nextPage, histPag.pageSize, histQ)
      })
      .catch((e) => message.error(e.message))
      .finally(() => setHistDeletingId(null))
  }

  const openCompareResult = (id: number) => {
    window.open(docCompareResultUrl(id), '_blank', 'noopener,noreferrer')
  }

  const loadLibrary = useCallback((page: number, ps: number, q: string) => {
    setLibLoading(true)
    api
      .get<PageResult<LibraryItem>>('/public/doc-compare/items', {
        params: { page, page_size: ps, q: q || undefined },
      })
      .then((r) => {
        setLibItems(r.data.items)
        setLibTotal(r.data.total)
      })
      .catch((e) => message.error(e.message))
      .finally(() => setLibLoading(false))
  }, [])

  useEffect(() => {
    if (!pickerOpen) return
    loadLibrary(libPag.page, libPag.pageSize, libQ)
  }, [pickerOpen, libPag.page, libPag.pageSize, libQ, loadLibrary])

  const openPicker = (side: 'left' | 'right') => {
    setPickerOpen(side)
    libPag.resetPage()
    loadLibrary(1, libPag.pageSize, libQ)
  }

  const uploadProps = (side: 'left' | 'right'): UploadProps => ({
    maxCount: 1,
    showUploadList: false,
    accept: COMPARE_ACCEPT,
    customRequest: ({ file, onSuccess, onError }) => {
      const fd = new FormData()
      fd.append('file', file as File)
      api
        .post<{ item_id: number; title: string }>('/public/doc-compare/upload', fd, {
          timeout: LONG_MS,
          headers: { 'Content-Type': 'multipart/form-data' },
        })
        .then((r) => {
          const patch = { kind: 'upload' as const, itemId: r.data.item_id, title: r.data.title }
          if (side === 'left') setLeft(patch)
          else setRight(patch)
          message.success(`已上传：${r.data.title}`)
          onSuccess?.(r.data)
        })
        .catch((e) => {
          message.error(e.message)
          onError?.(e)
        })
    },
  })

  const runCompare = () => {
    if (!left.itemId || !right.itemId) {
      message.warning('请为左右两侧各选择或上传一个文档')
      return
    }
    setLoading(true)
    api
      .post<SessionRunOut>('/public/doc-compare/run', {
        left: { kind: left.kind, item_id: left.itemId },
        right: { kind: right.kind, item_id: right.itemId },
      })
      .then((r) => {
        openCompareResult(r.data.id)
        histPag.resetPage()
        loadHistory(1, histPag.pageSize, histQ)
      })
      .catch((e) => message.error(e.message))
      .finally(() => setLoading(false))
  }

  const renderSourceTabs = (side: SideState, setSide: (s: SideState) => void) => (
    <div className="dc-source-tabs">
      {(['upload', 'library'] as const).map((k) => (
        <button
          key={k}
          type="button"
          className={`dc-source-tab${side.kind === k ? ' dc-source-tab--active' : ''}`}
          onClick={() => setSide({ kind: k, itemId: null, title: '' })}
        >
          {k === 'upload' ? '本地上传' : '企业库'}
        </button>
      ))}
    </div>
  )

  const renderSlotBody = (side: SideState, key: 'left' | 'right') => {
    if (side.itemId) {
      return (
        <div className="dc-file-card">
          <div className="dc-file-card-icon">
            <FileOutlined />
          </div>
          <span className="dc-file-card-name">{side.title}</span>
          <span className="dc-file-card-id">文档 ID {side.itemId}</span>
          <div className="dc-file-card-actions">
            {side.kind === 'upload' ? (
              <Upload {...uploadProps(key)}>
                <Button size="small" icon={<ReloadOutlined />}>
                  重新选择
                </Button>
              </Upload>
            ) : (
              <Button size="small" icon={<ReloadOutlined />} onClick={() => openPicker(key)}>
                重新选择
              </Button>
            )}
          </div>
        </div>
      )
    }
    if (side.kind === 'upload') {
      return (
        <Upload.Dragger {...uploadProps(key)}>
          <p className="ant-upload-drag-icon">
            <CloudUploadOutlined />
          </p>
          <p className="ant-upload-text">点击或拖拽文件到此处</p>
          <p className="ant-upload-hint">PDF · Word（.doc/.docx/.rtf）· 图片</p>
        </Upload.Dragger>
      )
    }
    return (
      <div
        className="dc-pick-zone"
        role="button"
        tabIndex={0}
        onClick={() => openPicker(key)}
        onKeyDown={(e) => e.key === 'Enter' && openPicker(key)}
      >
        <div className="dc-pick-icon-wrap">
          <FolderOpenOutlined />
        </div>
        <span className="dc-pick-label">从企业知识库选择</span>
        <span className="dc-pick-hint">浏览已入库的企业文档</span>
      </div>
    )
  }

  const renderSlot = (
    label: string,
    slotClass: string,
    side: SideState,
    setSide: (s: SideState) => void,
    key: 'left' | 'right',
  ) => (
    <div className={`dc-slot ${slotClass}`}>
      <div className="dc-slot-head">
        <div className="dc-slot-label">
          <span className="dc-slot-dot" />
          {label}
        </div>
        {renderSourceTabs(side, setSide)}
      </div>
      <div className="dc-slot-body">
        <div className="dc-slot-content">{renderSlotBody(side, key)}</div>
      </div>
    </div>
  )

  const ready = !!(left.itemId && right.itemId)

  return (
    <div className="doc-compare-page public-page-scroll">
      <PageHeader
        title="文档比对"
        description="上传或从企业知识库选择 PDF、Word、图片；统一转 PDF 后逐页 OCR 识别（含坐标），再按行比对并在页图上标注差异。"
      />

      <div className="dc-hero">
        <div className="dc-hero-icon">
          <DiffOutlined />
        </div>
        <div className="dc-hero-text">
          <span className="dc-hero-title">选择两份文档开始比对</span>
          <span className="dc-hero-desc">支持 PDF · Word · 图片；转 PDF 后逐页 OCR，三栏对照并在页图上标注差异</span>
        </div>
      </div>

      <div className="dc-slots">
        {renderSlot('基准文档', 'dc-slot--left', left, setLeft, 'left')}
        {renderSlot('比对文档', 'dc-slot--right', right, setRight, 'right')}
      </div>

      <div className="dc-action-bar">
        <Button
          type="primary"
          className="dc-run-btn"
          icon={<DiffOutlined />}
          loading={loading}
          disabled={!ready}
          onClick={runCompare}
        >
          开始比对
        </Button>
        <span className="dc-action-hint">
          {ready ? '两侧文档已就绪，点击开始比对' : '请为左右两侧各选择或上传一个文档'}
        </span>
      </div>

      <div className="dc-history">
        <div className="dc-history-head">
          <div className="dc-history-title">
            <HistoryOutlined />
            最近比对
            <span className="dc-history-badge">保留 7 天</span>
          </div>
          <Input.Search
            placeholder="搜索文档标题"
            allowClear
            style={{ width: 220 }}
            onSearch={(v) => {
              histPag.resetPage()
              setHistQ(v)
            }}
          />
        </div>
        <Table
          rowKey="id"
          size="middle"
          loading={histLoading}
          dataSource={histItems}
          locale={{ emptyText: '暂无比对记录' }}
          pagination={tablePagination(
            histPag.page,
            histTotal,
            (p, ps) => {
              histPag.handlePaginationChange(p, ps)
              loadHistory(p, ps ?? histPag.pageSize, histQ)
            },
            histPag.paginationOptions,
          )}
          columns={[
            {
              title: '比对文档',
              key: 'docs',
              ellipsis: true,
              render: (_, row) => (
                <div className="dc-doc-pair">
                  <div className="dc-doc-pair-item">
                    <span className="dc-doc-pair-tag dc-doc-pair-tag--left">左</span>
                    <Text ellipsis>{row.left_title}</Text>
                  </div>
                  <div className="dc-doc-pair-item">
                    <span className="dc-doc-pair-tag dc-doc-pair-tag--right">右</span>
                    <Text ellipsis>{row.right_title}</Text>
                  </div>
                </div>
              ),
            },
            {
              title: '类型',
              dataIndex: 'compare_mode',
              width: 72,
              render: (v: string) => (
                <Tag bordered={false} color={v === 'pdf' ? 'blue' : v === 'image' ? 'purple' : 'default'}>
                  {v === 'pdf' ? 'PDF' : v === 'image' ? '图片' : v === 'text' ? '文本' : v}
                </Tag>
              ),
            },
            {
              title: '比对时间',
              dataIndex: 'created_at',
              width: 160,
              render: (v: string | null) => formatDate(v),
            },
            {
              title: '操作',
              width: 96,
              align: 'center',
              render: (_, row) => (
                <div className="dc-history-actions">
                  <Button
                    type="link"
                    size="small"
                    icon={<EyeOutlined />}
                    title="查看"
                    onClick={() => openCompareResult(row.id)}
                  />
                  <Popconfirm
                    title="确定删除这条比对记录？"
                    description="删除后无法恢复；若为临时上传文档且未被其他记录引用，将一并清理。"
                    okText="删除"
                    cancelText="取消"
                    okButtonProps={{ danger: true, loading: histDeletingId === row.id }}
                    onConfirm={() => deleteHistorySession(row.id)}
                  >
                    <Button
                      type="link"
                      size="small"
                      danger
                      icon={<DeleteOutlined />}
                      title="删除"
                      loading={histDeletingId === row.id}
                    />
                  </Popconfirm>
                </div>
              ),
            },
          ]}
        />
      </div>

      <Drawer
        title="选择企业库文档"
        open={pickerOpen !== null}
        onClose={() => setPickerOpen(null)}
        width={520}
      >
        <Input.Search
          placeholder="搜索标题 / 标签"
          allowClear
          style={{ marginBottom: 12 }}
          onSearch={(v) => {
            libPag.resetPage()
            setLibQ(v)
          }}
          onChange={(e) => setLibQ(e.target.value)}
        />
        <Table
          rowKey="id"
          size="small"
          loading={libLoading}
          dataSource={libItems}
          pagination={tablePagination(
            libPag.page,
            libTotal,
            (p, ps) => {
              libPag.handlePaginationChange(p, ps)
              loadLibrary(p, ps ?? libPag.pageSize, libQ)
            },
            libPag.paginationOptions,
          )}
          columns={[
            { title: '标题', dataIndex: 'title', ellipsis: true },
            {
              title: '操作',
              width: 72,
              render: (_, row) => (
                <Button
                  type="link"
                  size="small"
                  onClick={() => {
                    if (!pickerOpen) return
                    const patch = { kind: 'library' as const, itemId: row.id, title: row.title }
                    if (pickerOpen === 'left') setLeft(patch)
                    else setRight(patch)
                    setPickerOpen(null)
                    message.success(`已选择：${row.title}`)
                  }}
                >
                  选择
                </Button>
              ),
            },
          ]}
        />
      </Drawer>
    </div>
  )
}
